"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { LocationDistrictVm } from "@/lib/location-header126";
const ROW_COUNT = 4;
const STEP_PX = 220;
const DRAG_THRESHOLD = 8;
function CheckIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M1.5 7.25 5 10.75 12.5 2.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg viewBox="0 0 14 14" fill="none" aria-hidden="true" className={className}>{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function DistrictChip(t0) {
  const $ = _c(20);
  const {
    district,
    isDark
  } = t0;
  const t1 = isDark ? "text-primary" : "text-black";
  let t2;
  if ($[0] !== t1) {
    t2 = ["size-3.5 shrink-0 md:size-4", t1];
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = t2.join(" ");
  let t4;
  if ($[2] !== t3) {
    t4 = <CheckIcon className={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== district.name) {
    t5 = <span className="whitespace-nowrap">{district.name}</span>;
    $[4] = district.name;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const content = t6;
  if (district.href) {
    const t7 = district.href;
    const t8 = isDark ? "text-primary" : "text-black";
    let t9;
    if ($[9] !== t8) {
      t9 = ["inline-flex shrink-0 items-center gap-2 text-sm transition hover:text-accent md:text-base", t8];
      $[9] = t8;
      $[10] = t9;
    } else {
      t9 = $[10];
    }
    const t10 = t9.join(" ");
    let t11;
    if ($[11] !== content || $[12] !== district.href || $[13] !== t10) {
      t11 = <Link href={t7} draggable={false} className={t10}>{content}</Link>;
      $[11] = content;
      $[12] = district.href;
      $[13] = t10;
      $[14] = t11;
    } else {
      t11 = $[14];
    }
    return t11;
  }
  const t7 = isDark ? "text-primary/80" : "text-black/70";
  let t8;
  if ($[15] !== t7) {
    t8 = ["inline-flex shrink-0 items-center gap-2 text-sm md:text-base", t7];
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = t8.join(" ");
  let t10;
  if ($[17] !== content || $[18] !== t9) {
    t10 = <span className={t9} aria-disabled="true">{content}</span>;
    $[17] = content;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
function splitIntoRows(items: LocationDistrictVm[]): LocationDistrictVm[][] {
  const rows: LocationDistrictVm[][] = Array.from({
    length: ROW_COUNT
  }, () => []);
  items.forEach((item, index) => {
    rows[index % ROW_COUNT].push(item);
  });
  return rows.map(row => {
    if (row.length === 0) return items;
    let padded = [...row];
    while (padded.length < 6) {
      padded = [...padded, ...row];
    }
    return padded;
  });
}
function normalizeOffset(value: number, halfWidth: number) {
  if (halfWidth <= 0) return value;
  let next = value;
  while (next <= -halfWidth) next += halfWidth;
  while (next > 0) next -= halfWidth;
  return next;
}
type DistrictsMarqueeProps = {
  districts: LocationDistrictVm[];
  isDark: boolean;
};
export function DistrictsMarquee(t0) {
  const $ = _c(47);
  const {
    districts,
    isDark
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const trackRefs = useRef(t1);
  const viewportRef = useRef(null);
  const halfWidthRef = useRef(0);
  const offsetRef = useRef(0);
  const pointerActiveRef = useRef(false);
  const draggingRef = useRef(false);
  const didDragRef = useRef(false);
  const startXRef = useRef(0);
  const lastPointerXRef = useRef(0);
  const [offset, setOffset] = useState(0);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const widths = trackRefs.current.map(_temp).filter(_temp2);
      halfWidthRef.current = widths.length > 0 ? Math.min(...widths) : 0;
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const measure = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (value, animate) => {
      const next = normalizeOffset(value, halfWidthRef.current);
      offsetRef.current = next;
      setOffset(next);
      trackRefs.current.forEach(track_0 => {
        if (!track_0) {
          return;
        }
        track_0.style.transition = animate ? "transform 420ms cubic-bezier(0.215, 0.61, 0.355, 1)" : "none";
        track_0.style.transform = `translate3d(${next}px,0,0)`;
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const applyOffset = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      measure();
      applyOffset(0, false);
      const tracks = trackRefs.current.filter(Boolean) as HTMLDivElement[];
      if (tracks.length === 0) {
        return;
      }
      const observer = new ResizeObserver(() => {
        measure();
        applyOffset(offsetRef.current, false);
      });
      tracks.forEach(track_1 => observer.observe(track_1));
      return () => observer.disconnect();
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== districts) {
    t5 = [applyOffset, districts, measure];
    $[4] = districts;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event => {
      if (event.button !== 0) {
        return;
      }
      pointerActiveRef.current = true;
      draggingRef.current = false;
      didDragRef.current = false;
      startXRef.current = event.clientX;
      lastPointerXRef.current = event.clientX;
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const onPointerDown = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event_0 => {
      if (!pointerActiveRef.current) {
        return;
      }
      const totalDelta = event_0.clientX - startXRef.current;
      if (!draggingRef.current) {
        if (Math.abs(totalDelta) < DRAG_THRESHOLD) {
          return;
        }
        draggingRef.current = true;
        didDragRef.current = true;
        trackRefs.current.forEach(_temp3);
        viewportRef.current?.setPointerCapture(event_0.pointerId);
        lastPointerXRef.current = event_0.clientX;
        return;
      }
      const delta = event_0.clientX - lastPointerXRef.current;
      lastPointerXRef.current = event_0.clientX;
      applyOffset(offsetRef.current + delta, false);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const onPointerMove = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = event_1 => {
      if (!pointerActiveRef.current) {
        return;
      }
      pointerActiveRef.current = false;
      if (draggingRef.current && viewportRef.current?.hasPointerCapture(event_1.pointerId)) {
        viewportRef.current.releasePointerCapture(event_1.pointerId);
      }
      draggingRef.current = false;
      window.setTimeout(() => {
        didDragRef.current = false;
      }, 0);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const endDrag = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = event_2 => {
      if (!didDragRef.current) {
        return;
      }
      event_2.preventDefault();
      event_2.stopPropagation();
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const onClickCapture = t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      applyOffset(offsetRef.current - STEP_PX, true);
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const shiftNext = t10;
  if (districts.length === 0) {
    return null;
  }
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[11] !== districts || $[12] !== isDark || $[13] !== offset) {
    const rows = splitIntoRows(districts);
    t20 = "flex flex-col gap-3 md:gap-3.5";
    t21 = "Dzielnice Wroc\u0142awia";
    t13 = viewportRef;
    t14 = "cursor-grab overflow-x-clip active:cursor-grabbing";
    t15 = onPointerDown;
    t16 = onPointerMove;
    t17 = endDrag;
    t18 = endDrag;
    t19 = onClickCapture;
    t11 = "flex flex-col gap-3 md:gap-3.5";
    t12 = rows.map((row, rowIndex) => {
      const sequence = [...row, ...row];
      return <div key={`district-row-${rowIndex}`} className="overflow-x-clip"><div ref={node => {
          trackRefs.current[rowIndex] = node;
        }} className="flex w-max select-none gap-x-5 will-change-transform md:gap-x-6" style={{
          transform: `translate3d(${offset}px,0,0)`
        }}>{sequence.map((district, index) => <DistrictChip key={`${district._key}-${index}`} district={district} isDark={isDark} />)}</div></div>;
    });
    $[11] = districts;
    $[12] = isDark;
    $[13] = offset;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
    $[18] = t15;
    $[19] = t16;
    $[20] = t17;
    $[21] = t18;
    $[22] = t19;
    $[23] = t20;
    $[24] = t21;
  } else {
    t11 = $[14];
    t12 = $[15];
    t13 = $[16];
    t14 = $[17];
    t15 = $[18];
    t16 = $[19];
    t17 = $[20];
    t18 = $[21];
    t19 = $[22];
    t20 = $[23];
    t21 = $[24];
  }
  let t22;
  if ($[25] !== t11 || $[26] !== t12) {
    t22 = <div className={t11}>{t12}</div>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  let t23;
  if ($[28] !== t13 || $[29] !== t14 || $[30] !== t15 || $[31] !== t16 || $[32] !== t17 || $[33] !== t18 || $[34] !== t19 || $[35] !== t22) {
    t23 = <div ref={t13} className={t14} onPointerDown={t15} onPointerMove={t16} onPointerUp={t17} onPointerCancel={t18} onClickCapture={t19}>{t22}</div>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  const t24 = isDark ? "text-primary hover:text-accent" : "text-black hover:text-accent";
  let t25;
  if ($[37] !== t24) {
    t25 = ["inline-flex items-center justify-center p-1 transition duration-300", t24];
    $[37] = t24;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  const t26 = t25.join(" ");
  let t27;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="size-6 md:size-7"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== t26) {
    t28 = <div className="flex justify-end pt-1"><button type="button" onClick={shiftNext} aria-label={"Przesu\u0144 list\u0119 dzielnic"} className={t26}>{t27}</button></div>;
    $[40] = t26;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  let t29;
  if ($[42] !== t20 || $[43] !== t21 || $[44] !== t23 || $[45] !== t28) {
    t29 = <div className={t20} aria-label={t21}>{t23}{t28}</div>;
    $[42] = t20;
    $[43] = t21;
    $[44] = t23;
    $[45] = t28;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  return t29;
}
function _temp3(track_2) {
  if (track_2) {
    track_2.style.transition = "none";
  }
}
function _temp2(width) {
  return width > 0;
}
function _temp(track) {
  return track ? track.scrollWidth / 2 : 0;
}